import { useState } from "react";
import { DotLegalFile, useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useUploadProgress } from "@common/components/uploadProgress/useUploadProgress";
import { isNullOrWhitespace } from "../../common/stringOperations";
import { sendWithFile } from "../../common/api/apiShared";
import { useTranslation } from "../../localization/useTranslation";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { BestPracticeDocumentTemplateViewModel, BestPracticeDocumentTemplateUpdateModel } from "../BestPracticeDocumentTemplates.types";

export interface EditBestPracticeDocumentTemplateDialogProps {
    onDialogClose: () => void;
    onSave: () => void;
    selectedTemplate: BestPracticeDocumentTemplateViewModel;
}

export function useEditBestPracticeDocumentTemplateDialog(props: EditBestPracticeDocumentTemplateDialogProps) {
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();

    const [viewModel, setViewModel] = useState<BestPracticeDocumentTemplateUpdateModel>({
        name: props.selectedTemplate.name,
        fileName: undefined,
    });
    const [file, setFile] = useState<File>();
    const [showError, setShowError] = useState(false);
    const { uploadPercentage, trackUploadProgress } = useUploadProgress();

    const updateMutation = useDotLegalMutation(update);

    const onNameChange = (name: string) => {
        let tempModel = { ...viewModel };
        tempModel.name = name;
        setViewModel(tempModel);
    };

    const onFileChange = (files: Array<DotLegalFile> | undefined) => {
        let tempModel = { ...viewModel };
        tempModel.fileName = files?.[0]?.fileName ?? "";
        setFile(files?.[0]?.file);
        setViewModel(tempModel);
    };

    const onOkClick = async () => {
        if (isNullOrWhitespace(viewModel.name)) {
            setShowError(true);
            return;
        }

        updateMutation.mutateAsync(props.selectedTemplate.id, {
            onSuccess: (response: any) => {
                if (response.success()) {
                    snackbar.show(translateString("bestPracticeDocumentTemplateUpdated"));
                    props.onSave();
                }
                if (response.error()) {
                    snackbar.show(translateString(response.error()[0]), "warning");
                }
            },
        });
    };

    return {
        viewModel,
        onNameChange,
        onFileChange,
        showError,
        inProgress: updateMutation.isLoading,
        isUploading: updateMutation.isLoading && file !== undefined,
        uploadPercentage,
        onOkClick,
    };

    function update(id: string) {
        return sendWithFile<string>("PUT", `/best-practice-document-templates/${id}`, viewModel, file, trackUploadProgress());
    }
}
